import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Avatar,
  Grid,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  Chip,
  Divider,
  Breadcrumbs,
  Link,
  IconButton,
  InputAdornment,
  Autocomplete,
  CircularProgress,
  Skeleton,
  Tab,
  Tabs,
  Alert,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
// PersonIcon removed (unused import)
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PublicIcon from '@mui/icons-material/Public';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LockIcon from '@mui/icons-material/Lock';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import HomeIcon from '@mui/icons-material/Home';
import BadgeIcon from '@mui/icons-material/Badge';
import SecurityIcon from '@mui/icons-material/Security';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import StarIcon from '@mui/icons-material/Star';
import DescriptionIcon from '@mui/icons-material/Description';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { API_BASE_URL } from '../../constants';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CountryOption {
  name: string;
  iso2?: string;
}

interface PasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const ROLE_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  admin: { label: 'Administrator', color: '#d32f2f', bg: '#fce4ec' },
  enterprise: { label: 'Enterprise', color: '#7b1fa2', bg: '#f3e5f5' },
  corporate: { label: 'Corporate', color: '#1565c0', bg: '#e3f2fd' },
  agent: { label: 'Agent', color: '#ef6c00', bg: '#fff3e0' },
  general: { label: 'Individual', color: '#2e7d32', bg: '#e8f5e9' },
};

const getInitials = (firstName?: string, lastName?: string): string => {
  const f = firstName?.charAt(0)?.toUpperCase() ?? '';
  const l = lastName?.charAt(0)?.toUpperCase() ?? '';
  return f + l || '?';
};

const formatDate = (iso?: string): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return '—';
  }
};

const toInputDate = (iso?: string): string => {
  if (!iso) return '';
  try {
    return new Date(iso).toISOString().split('T')[0];
  } catch {
    return '';
  }
};

// ---------------------------------------------------------------------------
// Shared sx values
// ---------------------------------------------------------------------------

const sectionCard = {
  borderRadius: 3,
  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  border: '1px solid',
  borderColor: 'grey.200',
  overflow: 'visible',
};

const sectionHeader = {
  pb: 0,
  '& .MuiCardHeader-title': { fontWeight: 600, fontSize: '1.1rem' },
  '& .MuiCardHeader-subheader': { fontSize: '0.85rem' },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, updateProfile, isLoading: authLoading } = useAuth();
  const { showSuccess, showError } = useNotification();

  // Personal info edit state
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    nationality: '',
    dateOfBirth: '',
  });

  // Password state
  const [passwordForm, setPasswordForm] = useState<PasswordForm>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [changingPassword, setChangingPassword] = useState(false);

  // Countries for nationality autocomplete
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [countriesLoading, setCountriesLoading] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Stats
  const [stats, setStats] = useState({ totalApplications: 0, approved: 0, pending: 0, creditsBalance: 0 });

  // Populate form when user data is available
  useEffect(() => {
    if (user) {
      setForm({
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        phone: user.phone ?? '',
        nationality: user.nationality ?? '',
        dateOfBirth: toInputDate(user.dateOfBirth),
      });
    }
  }, [user]);

  // Fetch countries once
  const fetchCountries = useCallback(async () => {
    if (countries.length > 0) return;
    setCountriesLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/countries`);
      if (res.ok) {
        const json = await res.json();
        const list: CountryOption[] = (Array.isArray(json) ? json : json.data ?? []).map(
          (c: any) => ({ name: c.name ?? c.country_name ?? c.label ?? '', iso2: c.iso2 ?? c.code ?? '' }),
        );
        setCountries(list);
      }
    } catch {
      // Silent – user can still type manually
    } finally {
      setCountriesLoading(false);
    }
  }, [countries.length]);

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  // Fetch stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem('tripnovac_auth_token');
        const res = await fetch(`${API_BASE_URL}/dashboard/individual`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const json = await res.json();
          const d = json.data || json;
          setStats({
            totalApplications: d.totalApplications ?? d.total_applications ?? 0,
            approved: d.approved ?? d.approved_applications ?? 0,
            pending: d.pending ?? d.pending_applications ?? 0,
            creditsBalance: d.creditsBalance ?? d.credits_balance ?? 0,
          });
        }
      } catch { /* silent */ }
    };
    fetchStats();
  }, []);

  // -----------------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------------

  const handleEditToggle = () => {
    if (editing && user) {
      // Reset form
      setForm({
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        phone: user.phone ?? '',
        nationality: user.nationality ?? '',
        dateOfBirth: toInputDate(user.dateOfBirth),
      });
    }
    setEditing((prev) => !prev);
  };

  const handleSave = async () => {
    if (!form.firstName.trim() || !form.lastName.trim()) {
      showError('First name and last name are required.');
      return;
    }
    setSaving(true);
    try {
      const payload: Record<string, string> = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
      };
      if (form.phone.trim()) payload.phone = form.phone.trim();
      if (form.nationality.trim()) payload.nationality = form.nationality.trim();
      if (form.dateOfBirth) payload.dateOfBirth = form.dateOfBirth;

      const result = await updateProfile(payload);
      if (result.success) {
        showSuccess(result.message || 'Profile updated successfully.');
        setEditing(false);
      } else {
        showError(result.message || 'Failed to update profile.');
      }
    } catch {
      showError('An unexpected error occurred while saving.');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    const { currentPassword, newPassword, confirmPassword } = passwordForm;
    if (!currentPassword || !newPassword || !confirmPassword) {
      showError('Please fill in all password fields.');
      return;
    }
    if (newPassword.length < 8) {
      showError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      showError('New passwords do not match.');
      return;
    }

    setChangingPassword(true);
    try {
      const token = localStorage.getItem('tripnovac_auth_token');
      const res = await fetch(`${API_BASE_URL}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok) {
        showSuccess(json.message || 'Password changed successfully.');
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        showError(json.message || 'Failed to change password.');
      }
    } catch {
      showError('Network error. Please try again.');
    } finally {
      setChangingPassword(false);
    }
  };

  // -----------------------------------------------------------------------
  // Role badge
  // -----------------------------------------------------------------------

  const roleCfg = ROLE_CONFIG[user?.role ?? 'general'] ?? ROLE_CONFIG.general;

  // -----------------------------------------------------------------------
  // Loading skeleton
  // -----------------------------------------------------------------------

  if (authLoading && !user) {
    return (
      <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 2, md: 4 } }}>
        <Skeleton variant="rounded" height={180} sx={{ mb: 3, borderRadius: 3 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rounded" height={350} sx={{ borderRadius: 3 }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rounded" height={350} sx={{ borderRadius: 3 }} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <Box sx={{ bgcolor: '#f5f7fa', minHeight: '100vh', pb: 6 }}>
      {/* ===== Hero Section ===== */}
      <Box
        sx={{
          backgroundImage: 'url("https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1920&q=80")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: '#fff',
          minHeight: 200,
          pt: { xs: 4, md: 5 },
          pb: { xs: 6, md: 7 },
          px: { xs: 2, md: 4 },
        }}
      >
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          {/* Breadcrumbs */}
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.6)' }} />}
            sx={{ mb: 3 }}
          >
            <Link
              underline="hover"
              onClick={() => navigate('/')}
              sx={{ display: 'flex', alignItems: 'center', color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', cursor: 'pointer' }}
            >
              <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
              Home
            </Link>
            <Typography sx={{ color: '#fff', fontWeight: 500, fontSize: '0.85rem' }}>My Profile</Typography>
          </Breadcrumbs>

          {/* Profile summary */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
            <Avatar
              sx={{
                width: 90,
                height: 90,
                fontSize: '2rem',
                fontWeight: 700,
                bgcolor: 'rgba(255,255,255,0.2)',
                border: '3px solid rgba(255,255,255,0.5)',
              }}
            >
              {getInitials(user?.firstName, user?.lastName)}
            </Avatar>

            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 0.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em', textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
                  {user?.firstName} {user?.lastName}
                </Typography>
                <Chip
                  label={roleCfg.label}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    color: '#fff',
                    bgcolor: 'rgba(255,255,255,0.22)',
                    backdropFilter: 'blur(4px)',
                    fontSize: '0.75rem',
                  }}
                />
              </Box>
              <Typography sx={{ opacity: 0.85, fontSize: '0.95rem', textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>{user?.email}</Typography>
              <Typography sx={{ opacity: 0.6, fontSize: '0.8rem', mt: 0.5, textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
                Member since {formatDate(user?.createdAt)}
              </Typography>
            </Box>

            {user?.isEmailVerified && (
              <Chip
                icon={<CheckCircleIcon sx={{ color: '#66bb6a !important' }} />}
                label="Verified"
                variant="outlined"
                size="small"
                sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)', fontSize: '0.8rem' }}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* ===== Stat Cards ===== */}
      <Box sx={{ maxWidth: 1100, mx: 'auto', mt: -4, px: { xs: 2, md: 4 }, mb: 3 }}>
        <Grid container spacing={2}>
          {[
            { label: 'Total Applications', value: stats.totalApplications, icon: <DescriptionIcon /> },
            { label: 'Approved', value: stats.approved, icon: <CheckCircleIcon /> },
            { label: 'Pending', value: stats.pending, icon: <AccessTimeIcon /> },
            { label: 'Credits Balance', value: stats.creditsBalance, icon: <AccountBalanceWalletIcon /> },
          ].map((s) => (
            <Grid item xs={6} md={3} key={s.label}>
              <Card
                sx={{
                  background: 'linear-gradient(135deg, #00897b 0%, #00695c 100%)',
                  color: '#fff',
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(0,137,123,0.3)',
                }}
              >
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, '&:last-child': { pb: 2 } }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      bgcolor: 'rgba(255,255,255,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {s.icon}
                  </Box>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {s.value}
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem', opacity: 0.85 }}>{s.label}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ===== Quick Actions ===== */}
      <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 4 }, mb: 3 }}>
        <Grid container spacing={2}>
          {[
            { label: '+ New Application', href: '/applications/new', icon: <AddIcon /> },
            { label: 'My Applications', href: '/applications', icon: <AssignmentIcon /> },
            { label: 'Buy Credits', href: '/credits/purchase', icon: <AccountBalanceWalletIcon /> },
            { label: 'Saved Countries', href: '/saved', icon: <BookmarkBorderIcon /> },
          ].map((a) => (
            <Grid item xs={6} md={3} key={a.label}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={a.icon}
                href={a.href}
                sx={{
                  textTransform: 'none',
                  borderColor: 'grey.300',
                  color: 'text.primary',
                  borderRadius: 2,
                  py: 1.2,
                  '&:hover': { borderColor: '#1565c0', color: '#1565c0' },
                }}
              >
                {a.label}
              </Button>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ===== Content ===== */}
      <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 4 } }}>
        <Grid container spacing={3}>
          {/* ---------- Left Column (Tabs + Security) ---------- */}
          <Grid item xs={12} md={8}>
            {/* Tabbed Info Card */}
            <Card sx={sectionCard}>
              <Tabs
                value={activeTab}
                onChange={(_e, v) => setActiveTab(v)}
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  px: 2,
                  '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.9rem' },
                }}
              >
                <Tab label="Personal Information" />
                <Tab label="Passport Details" />
                <Tab label="Preferences" />
              </Tabs>

              <CardContent sx={{ pt: 2 }}>
                {/* ---- Tab 0: Personal Information ---- */}
                {activeTab === 0 && (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                      {!editing ? (
                        <IconButton onClick={handleEditToggle} size="small" sx={{ color: '#1565c0' }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton onClick={handleSave} size="small" color="primary" disabled={saving}>
                            {saving ? <CircularProgress size={18} /> : <SaveIcon fontSize="small" />}
                          </IconButton>
                          <IconButton onClick={handleEditToggle} size="small" color="default">
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="First Name"
                          value={form.firstName}
                          onChange={(e) => setForm((p) => ({ ...p, firstName: e.target.value }))}
                          fullWidth
                          size="small"
                          disabled={!editing}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <BadgeIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Last Name"
                          value={form.lastName}
                          onChange={(e) => setForm((p) => ({ ...p, lastName: e.target.value }))}
                          fullWidth
                          size="small"
                          disabled={!editing}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <BadgeIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Email Address"
                          value={user?.email ?? ''}
                          fullWidth
                          size="small"
                          disabled
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <EmailIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                              </InputAdornment>
                            ),
                            endAdornment: user?.isEmailVerified ? (
                              <InputAdornment position="end">
                                <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                              </InputAdornment>
                            ) : undefined,
                          }}
                          helperText={user?.isEmailVerified ? 'Email verified' : 'Email not verified'}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Phone Number"
                          value={form.phone}
                          onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                          fullWidth
                          size="small"
                          disabled={!editing}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PhoneIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Autocomplete
                          freeSolo
                          disabled={!editing}
                          options={countries}
                          getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.name)}
                          value={countries.find((c) => c.name === form.nationality) ?? (form.nationality || null)}
                          onChange={(_e, val) => {
                            const name = typeof val === 'string' ? val : val?.name ?? '';
                            setForm((p) => ({ ...p, nationality: name }));
                          }}
                          onInputChange={(_e, val) => setForm((p) => ({ ...p, nationality: val }))}
                          loading={countriesLoading}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Nationality"
                              size="small"
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <>
                                    <InputAdornment position="start">
                                      <PublicIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                                    </InputAdornment>
                                    {params.InputProps.startAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Date of Birth"
                          type="date"
                          value={form.dateOfBirth}
                          onChange={(e) => setForm((p) => ({ ...p, dateOfBirth: e.target.value }))}
                          fullWidth
                          size="small"
                          disabled={!editing}
                          InputLabelProps={{ shrink: true }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <CalendarTodayIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>

                    {editing && (
                      <Box sx={{ display: 'flex', gap: 1.5, mt: 3, justifyContent: 'flex-end' }}>
                        <Button variant="outlined" size="small" onClick={handleEditToggle} disabled={saving}>
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleSave}
                          disabled={saving}
                          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                        >
                          Save Changes
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}

                {/* ---- Tab 1: Passport Details ---- */}
                {activeTab === 1 && (
                  <Box>
                    <Alert severity="info" sx={{ mb: 2, fontSize: '0.85rem' }}>
                      Passport details can be updated when submitting a visa application.
                    </Alert>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField label="Passport Number" fullWidth size="small" disabled placeholder="—" />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField label="Issuing Country" fullWidth size="small" disabled placeholder="—" />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Issue Date"
                          type="date"
                          fullWidth
                          size="small"
                          disabled
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Expiry Date"
                          type="date"
                          fullWidth
                          size="small"
                          disabled
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {/* ---- Tab 2: Preferences ---- */}
                {activeTab === 2 && (
                  <Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Language</InputLabel>
                          <Select defaultValue="en" label="Language">
                            <MenuItem value="en">English</MenuItem>
                            <MenuItem value="fr">French</MenuItem>
                            <MenuItem value="es">Spanish</MenuItem>
                            <MenuItem value="ar">Arabic</MenuItem>
                            <MenuItem value="zh">Chinese</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Currency</InputLabel>
                          <Select defaultValue="USD" label="Currency">
                            <MenuItem value="USD">USD – US Dollar</MenuItem>
                            <MenuItem value="EUR">EUR – Euro</MenuItem>
                            <MenuItem value="GBP">GBP – British Pound</MenuItem>
                            <MenuItem value="AED">AED – UAE Dirham</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          Notifications
                        </Typography>
                        <FormControlLabel
                          control={<Switch defaultChecked />}
                          label="Email notifications"
                          sx={{ display: 'block' }}
                        />
                        <FormControlLabel
                          control={<Switch />}
                          label="SMS notifications"
                          sx={{ display: 'block' }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* ---------- Account Security ---------- */}
            <Card sx={{ ...sectionCard, mt: 3 }}>
              <CardHeader
                avatar={<SecurityIcon sx={{ color: '#1565c0' }} />}
                title="Account Security"
                subheader="Update your password to keep your account secure"
                sx={sectionHeader}
              />
              <CardContent sx={{ pt: 2 }}>
                <Grid container spacing={2} sx={{ maxWidth: 600 }}>
                  <Grid item xs={12}>
                    <TextField
                      label="Current Password"
                      type={showPasswords.current ? 'text' : 'password'}
                      value={passwordForm.currentPassword}
                      onChange={(e) =>
                        setPasswordForm((p) => ({ ...p, currentPassword: e.target.value }))
                      }
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() =>
                                setShowPasswords((p) => ({ ...p, current: !p.current }))
                              }
                              edge="end"
                            >
                              {showPasswords.current ? (
                                <VisibilityOffIcon fontSize="small" />
                              ) : (
                                <VisibilityIcon fontSize="small" />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="New Password"
                      type={showPasswords.new ? 'text' : 'password'}
                      value={passwordForm.newPassword}
                      onChange={(e) =>
                        setPasswordForm((p) => ({ ...p, newPassword: e.target.value }))
                      }
                      fullWidth
                      size="small"
                      helperText="Minimum 8 characters"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() =>
                                setShowPasswords((p) => ({ ...p, new: !p.new }))
                              }
                              edge="end"
                            >
                              {showPasswords.new ? (
                                <VisibilityOffIcon fontSize="small" />
                              ) : (
                                <VisibilityIcon fontSize="small" />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Confirm New Password"
                      type={showPasswords.confirm ? 'text' : 'password'}
                      value={passwordForm.confirmPassword}
                      onChange={(e) =>
                        setPasswordForm((p) => ({ ...p, confirmPassword: e.target.value }))
                      }
                      fullWidth
                      size="small"
                      error={
                        !!passwordForm.confirmPassword &&
                        passwordForm.newPassword !== passwordForm.confirmPassword
                      }
                      helperText={
                        passwordForm.confirmPassword &&
                        passwordForm.newPassword !== passwordForm.confirmPassword
                          ? 'Passwords do not match'
                          : ' '
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() =>
                                setShowPasswords((p) => ({ ...p, confirm: !p.confirm }))
                              }
                              edge="end"
                            >
                              {showPasswords.confirm ? (
                                <VisibilityOffIcon fontSize="small" />
                              ) : (
                                <VisibilityIcon fontSize="small" />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleChangePassword}
                      disabled={
                        changingPassword ||
                        !passwordForm.currentPassword ||
                        !passwordForm.newPassword ||
                        !passwordForm.confirmPassword
                      }
                      startIcon={changingPassword ? <CircularProgress size={16} /> : <LockIcon />}
                      sx={{ textTransform: 'none' }}
                    >
                      {changingPassword ? 'Updating…' : 'Change Password'}
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* ---------- Right Column ---------- */}
          <Grid item xs={12} md={4}>
            {/* Member Benefits */}
            <Card sx={sectionCard}>
              <CardHeader
                avatar={<StarIcon sx={{ color: '#f9a825' }} />}
                title="Member Benefits"
                subheader="Your membership perks"
                sx={sectionHeader}
              />
              <CardContent sx={{ pt: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {[
                    'Priority visa processing',
                    'Dedicated support team',
                    'AI-powered document review',
                    'Real-time application tracking',
                    'Exclusive travel deals',
                    'Multi-visa management',
                  ].map((benefit) => (
                    <Box key={benefit} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <StarIcon sx={{ fontSize: 16, color: '#f9a825' }} />
                      <Typography variant="body2">{benefit}</Typography>
                    </Box>
                  ))}
                </Box>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    mt: 2.5,
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #1565c0, #0d47a1)',
                    borderRadius: 2,
                  }}
                  onClick={() => navigate('/pricing')}
                >
                  Upgrade Plan
                </Button>
              </CardContent>
            </Card>

            {/* Account Details */}
            <Card sx={{ ...sectionCard, mt: 3 }}>
              <CardHeader
                avatar={<ContactMailIcon sx={{ color: '#1565c0' }} />}
                title="Account Details"
                subheader="Your account information"
                sx={sectionHeader}
              />
              <CardContent sx={{ pt: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Role
                    </Typography>
                    <Chip
                      label={roleCfg.label}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        color: roleCfg.color,
                        bgcolor: roleCfg.bg,
                        fontSize: '0.75rem',
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Member Since
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {formatDate(user?.createdAt)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Last Updated
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {formatDate(user?.updatedAt)}
                    </Typography>
                  </Box>
                  {user?.companyName && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Company
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {user.companyName}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      User ID
                    </Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {user?.id ? `${user.id.slice(0, 8)}…` : '—'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ProfilePage;
